// src/pages/Chat/MessageInput.jsx
//
// Message composer. Sends via WebSocket (not REST) using the `send` prop.
// Handles: plain send, reply-to, edit mode, typing indicators, multiline.
// Features: E2EE Encryption via ChatCrypto.

import React, { useState, useRef, useEffect, useCallback } from "react";
import VoiceRecorder from "./VoiceRecorder";
import ContactPickerModal from "./ContactPickerModal";
import PollModal from "./PollModal";
import CameraCaptureModal from "./CameraCaptureModal";
import useChatStore from "../../store/chatStore";
import { ChatCrypto } from "../../utils/crypto";
import EmojiPicker from "./EmojiPicker";
import { uploadAttachment } from "../../api/chatService";
import { FiSend, FiPaperclip, FiSmile, FiX, FiMic, FiFile, FiImage, FiCamera, FiMusic, FiUser, FiBarChart2, FiCalendar } from "react-icons/fi";

const TYPING_THROTTLE_MS = 2000; // only send typing.start every 2s

export default function MessageInput({
  send,
  connected,
  replyTo,
  editTarget,
  onCancelReply,
  onCancelEdit,
  onEditSubmit,
}) {
  const selectedConvId = useChatStore((s) => s.selectedConvId);
  const currentUserName = useChatStore((s) => s.currentUserName);
  const convKeys = useChatStore((s) => s.conversationKeys);

  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [showPollModal, setShowPollModal] = useState(false);
  const [pollInitialData, setPollInitialData] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]); // Array of { id, original_filename }
  const [showEmoji, setShowEmoji] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const textareaRef = useRef(null);
  const typingTimer = useRef(null);
  const lastTypingSent = useRef(0);
  const isTypingRef = useRef(false);
  const fileInputRef = useRef(null);
  const mediaInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const menuRef = useRef(null);

  // Click outside menu closer
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowAttachmentMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // When edit mode activates, pre-fill the textarea
  useEffect(() => {
    if (editTarget) {
      if (editTarget.msg_type === "POLL") {
        setText("");
        try {
          const pollData = JSON.parse(editTarget.decryptedContent || editTarget.content);
          setPollInitialData(pollData);
          setShowPollModal(true);
        } catch (e) { }
      } else {
        setText(editTarget.decryptedContent || editTarget.content || "");
        textareaRef.current?.focus();
      }
    } else {
      setText("");
      setPollInitialData(null);
    }
  }, [editTarget]);

  // Clear text + pending attachment when conversation changes
  useEffect(() => {
    setText("");
    setPendingAttachments([]);
    setShowAttachmentMenu(false);
    setIsRecording(false);
    setShowContactPicker(false);
    setShowPollModal(false);
    setShowCamera(false);
    stopTyping();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConvId]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    if (text) {
      el.style.height = Math.min(el.scrollHeight, 120) + "px";
    }
  }, [text]);

  // ── Typing indicators ─────────────────────────────────────────────────────
  const sendTypingStart = useCallback(() => {
    if (!selectedConvId || !connected) return;
    const now = Date.now();
    if (now - lastTypingSent.current > TYPING_THROTTLE_MS) {
      send({ type: "typing_start", conv_id: selectedConvId });
      lastTypingSent.current = now;
      isTypingRef.current = true;
    }
  }, [send, selectedConvId, connected]);

  const stopTyping = useCallback(() => {
    clearTimeout(typingTimer.current);
    if (isTypingRef.current && selectedConvId) {
      send({ type: "typing_stop", conv_id: selectedConvId });
      isTypingRef.current = false;
    }
  }, [send, selectedConvId]);

  const handleChange = (e) => {
    setText(e.target.value);
    if (e.target.value.trim()) {
      sendTypingStart();
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(stopTyping, 3000);
    } else {
      stopTyping();
    }
  };



  const handleSendSticker = useCallback(async (url) => {
    if (!connected || !selectedConvId) return;

    let payload = {
      type: "send_message",
      conv_id: selectedConvId,
      content: url,
      ciphertext: "",
      iv: "",
      signature: "",
      msg_type: "STICKER",
      client_msg_id: crypto.randomUUID(),
      sender_name: currentUserName,
    };

    // ── E2EE Encryption ──────────────────────────────────────────────────────
    const aesKey = convKeys[selectedConvId];
    if (aesKey && url) {
      try {
        const { ciphertext, iv } = await ChatCrypto.encrypt(url, aesKey);
        payload.ciphertext = ciphertext;
        payload.iv = iv;
      } catch (err) {
        console.error("[Crypto] Encryption failed:", err);
      }
    }

    // Optimistic UI update
    const optimisticMessage = {
      id: "opt_" + payload.client_msg_id,
      conversation: selectedConvId,
      content: url,
      decryptedContent: url,
      ciphertext: payload.ciphertext,
      iv: payload.iv,
      msg_type: "STICKER",
      client_msg_id: payload.client_msg_id,
      reply_to: null,
      attachments: [],
      sender_name: currentUserName,
      sender: {
        employee_id: useChatStore.getState().currentUserId,
        full_name: currentUserName,
      },
      created_at: new Date().toISOString(),
      is_optimistic: true,
      is_delivered: false,
      is_read: false,
      is_read_by_me: true,
    };
    useChatStore.getState().appendMessage(selectedConvId, optimisticMessage);
    useChatStore.getState().updateConversationLastMessage(selectedConvId, optimisticMessage);

    send(payload);
    setShowEmoji(false);
  }, [connected, selectedConvId, currentUserName, convKeys, send]);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSend = useCallback(async (e, attachmentOverride = null) => {
    if (e) e.preventDefault();
    const trimmed = text.trim();
    const activeAttachments = attachmentOverride ? [attachmentOverride] : pendingAttachments;

    if (!trimmed && activeAttachments.length === 0 && !editTarget) return;
    if (sending || isUploading) return;

    stopTyping();

    // ── Edit mode ──────────────────────────────────────────────────────────
    if (editTarget) {
      setSending(true);
      try {
        await onEditSubmit?.(editTarget.id, trimmed);
        setText("");
      } finally {
        setSending(false);
      }
      return;
    }

    // ── Normal send via WebSocket ──────────────────────────────────────────
    if (!connected || !selectedConvId) return;

    // Detect message type
    let msg_type = "TEXT";
    if (activeAttachments.length > 0) {
      const firstAttachment = activeAttachments[0];
      if (firstAttachment.mime_category === "audio") msg_type = "VOICE";
      else msg_type = "FILE";
    }

    let payload = {
      type: "send_message",
      conv_id: selectedConvId,
      content: trimmed,
      ciphertext: "",
      iv: "",
      signature: "",
      msg_type: msg_type,
      client_msg_id: crypto.randomUUID(),
      reply_to_id: replyTo?.id ?? null,
      attachment_ids: activeAttachments.map(a => a.id),
      sender_name: currentUserName,
    };

    // ── E2EE Encryption ──────────────────────────────────────────────────────
    const aesKey = convKeys[selectedConvId];
    if (aesKey && trimmed) {
      try {
        const { ciphertext, iv } = await ChatCrypto.encrypt(trimmed, aesKey);
        payload.ciphertext = ciphertext;
        payload.iv = iv;
      } catch (err) {
        console.error("[Crypto] Encryption failed:", err);
      }
    }

    // Optimistic UI update
    const optimisticMessage = {
      id: "opt_" + payload.client_msg_id,
      conversation: selectedConvId,
      content: trimmed,
      decryptedContent: trimmed,
      ciphertext: payload.ciphertext,
      iv: payload.iv,
      msg_type: msg_type,
      client_msg_id: payload.client_msg_id,
      reply_to: replyTo ? {
        id: replyTo.id,
        content: replyTo.content,
        decryptedContent: replyTo.decryptedContent,
        sender_name: replyTo.sender_name,
        msg_type: replyTo.msg_type,
      } : null,
      attachments: activeAttachments || [],
      sender_name: currentUserName,
      sender: {
        employee_id: useChatStore.getState().currentUserId,
        full_name: currentUserName,
      },
      created_at: new Date().toISOString(),
      is_optimistic: true,
      is_delivered: false,
      is_read: false,
      is_read_by_me: true,
    };
    useChatStore.getState().appendMessage(selectedConvId, optimisticMessage);
    useChatStore.getState().updateConversationLastMessage(selectedConvId, optimisticMessage);

    send(payload);
    setText("");
    setPendingAttachments([]);
    setShowAttachmentMenu(false);
    onCancelReply?.();
  }, [text, selectedConvId, editTarget, connected, send, replyTo, pendingAttachments, stopTyping, onEditSubmit, onCancelReply, currentUserName, convKeys, sending, isUploading]);

  // ── File Upload Logic ──────────────────────────────────────────────────────
  const handleFileUpload = async (file) => {
    if (!file || !selectedConvId) return;
    setIsUploading(true);
    setUploadProgress(0);
    setShowAttachmentMenu(false);

    try {
      const aesKey = convKeys[selectedConvId];
      const formData = new FormData();
      let fileToUpload = file;

      if (aesKey) {
        const encrypted = await ChatCrypto.encryptFile(file, aesKey);
        fileToUpload = encrypted.encryptedBlob;
        formData.append("encrypted_file_key", encrypted.encryptedFileKey);
        formData.append("file_key_iv", encrypted.fileKeyIv);
        formData.append("file_iv", encrypted.fileIv);
      }

      const getMimeCategory = (f) => {
        if (f.type.startsWith("image/")) return "image";
        if (f.type.startsWith("video/")) return "video";
        if (f.type.startsWith("audio/")) return "audio";
        return "document";
      };

      formData.append("file", fileToUpload, file.name + (aesKey ? ".enc" : ""));
      formData.append("original_filename", file.name);
      formData.append("file_size", fileToUpload.size);
      formData.append("file_ext", file.name.split(".").pop()?.toLowerCase() || "");
      formData.append("conversation_id", selectedConvId);
      formData.append("mime_category", getMimeCategory(file));

      const { data } = await uploadAttachment(formData, (pct) => setUploadProgress(pct));
      setPendingAttachments(prev => [...prev, data]);
      setIsUploading(false);
      return data;
    } catch (err) {
      console.error("Upload failed:", err);
      setIsUploading(false);

      let errorMsg = "Upload failed. Please try again.";
      if (err?.response?.data) {
        if (typeof err.response.data === "string") {
          errorMsg = err.response.data;
        } else if (err.response.data.error) {
          errorMsg = err.response.data.error;
        } else if (err.response.data.detail) {
          errorMsg = err.response.data.detail;
        } else {
          // Parse validation dictionaries gracefully
          errorMsg = Object.entries(err.response.data)
            .map(([field, msgs]) => {
              const fieldLabel = field.replace(/_/g, " ").toUpperCase();
              const msgText = Array.isArray(msgs) ? msgs.join(", ") : String(msgs);
              return `${fieldLabel}: ${msgText}`;
            })
            .join("\n");
        }
      }
      alert(errorMsg);
      return null;
    }
  };

  const handleFileUploads = async (fileList) => {
    if (!fileList || !fileList.length) return;
    const filesArray = Array.from(fileList);
    for (let i = 0; i < filesArray.length; i++) {
      await handleFileUpload(filesArray[i]);
    }
    textareaRef.current?.focus();
  };

  const handleVoiceNote = async (audioBlob) => {
    const file = new File([audioBlob], `voice_note_${Date.now()}.webm`, { type: "audio/webm" });
    setIsRecording(false);
    const uploaded = await handleFileUpload(file);
    if (uploaded) {
      // Auto-send voice note
      handleSend(null, uploaded);
    }
  };

  const handleCapture = async (blob) => {
    const file = new File([blob], `capture_${Date.now()}.jpg`, { type: "image/jpeg" });
    await handleFileUpload(file);
    setShowCamera(false);
  };

  const handleSelectContact = (user) => {
    // For now, just send a text message with contact details
    // A better way would be a specific msg_type="CONTACT"
    const contactInfo = `Contact: ${user.full_name}\nID: ${user.employee_id}\nDept: ${user.department}`;
    setText(contactInfo);
    setShowContactPicker(false);
  };

  const handleCreatePoll = async (pollData) => {
    if (!selectedConvId || !connected) return;

    const isEdit = editTarget && editTarget.msg_type === "POLL";

    const pollPayload = {
      question: pollData.question,
      options: pollData.options,
      poll_id: isEdit ? pollInitialData?.poll_id : crypto.randomUUID(),
      is_poll: true
    };

    const strPayload = JSON.stringify(pollPayload);

    if (isEdit) {
      setSending(true);
      try {
        await onEditSubmit?.(editTarget.id, strPayload);
      } finally {
        setSending(false);
        setShowPollModal(false);
        onCancelEdit?.();
      }
      return;
    }

    let payload = {
      type: "send_message",
      conv_id: selectedConvId,
      content: strPayload,
      ciphertext: "",
      iv: "",
      signature: "",
      msg_type: "POLL",
      client_msg_id: crypto.randomUUID(),
      sender_name: currentUserName,
    };

    const aesKey = convKeys[selectedConvId];
    if (!aesKey) {
      console.error("Cannot send poll: E2E Encryption key is missing.");
      alert("Error: Conversation encryption key is missing or failed to decrypt. Please reload or contact support.");
      setShowPollModal(false);
      return;
    }

    try {
      const { ciphertext, iv } = await ChatCrypto.encrypt(strPayload, aesKey);
      payload.ciphertext = ciphertext;
      payload.iv = iv;
      payload.content = ""; // Ensure content is strictly empty for E2EE messages
    } catch (err) {
      console.error("Poll encryption failed:", err);
      alert("Failed to encrypt poll data. Cannot send.");
      setShowPollModal(false);
      return;
    }

    send(payload);
    setShowPollModal(false);
  };

  // ── Keyboard handler ──────────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === "Escape") {
      if (editTarget) { onCancelEdit?.(); setText(""); }
      if (replyTo) { onCancelReply?.(); }
    }
  };

  const canSend = (text.trim().length > 0 || pendingAttachments.length > 0) && (connected || !!editTarget);
  const isEdit = !!editTarget;

  return (
    <div className="shrink-0 px-4 pb-4 pt-2">
      {/* ── Reply banner ── */}
      {replyTo && !isEdit && (
        <div className="flex items-center justify-between px-3 py-1.5 mb-1.5 rounded-xl bg-blue-600/10 border border-blue-500/20 text-xs">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-blue-400 font-medium shrink-0">↩ Replying to</span>
            <span className="text-slate-300 truncate">
              {replyTo.sender_name || replyTo.sender?.full_name || "someone"}
            </span>
            <span className="text-slate-500 truncate">
              — {replyTo.content ? replyTo.content.slice(0, 60) : "[encrypted]"}
            </span>
          </div>
          <button
            onClick={onCancelReply}
            className="ml-2 p-1 rounded text-slate-500 hover:text-slate-300 hover:bg-white/10 transition-colors shrink-0"
          >
            <FiX size={11} />
          </button>
        </div>
      )}

      {/* ── Edit banner ── */}
      {isEdit && (
        <div className="flex items-center justify-between px-3 py-1.5 mb-1.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs">
          <span className="text-amber-400 font-medium">✏️ Editing message</span>
          <button
            onClick={() => { onCancelEdit?.(); setText(""); }}
            className="ml-2 p-1 rounded text-slate-500 hover:text-slate-300 hover:bg-white/10 transition-colors"
          >
            <FiX size={11} />
          </button>
        </div>
      )}

      {/* ── Progress bar ── */}
      {isUploading && (
        <div className="px-3 mb-2">
          <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
            <span>Uploading...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="h-1 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 transition-all duration-200" style={{ width: `${uploadProgress}%` }} />
          </div>
        </div>
      )}

      {/* ── Pending attachment badge ── */}
      {pendingAttachments.length > 0 && !isEdit && (
        <div className="flex flex-col gap-1 mb-1.5">
          {pendingAttachments.map(attachment => (
            <div key={attachment.id} className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-600/10 border border-blue-500/20 text-xs animate-in slide-in-from-left-2">
              <span className="text-blue-400 font-medium shrink-0">📎</span>
              <span className="text-slate-300 truncate">{attachment.original_filename}</span>
              <button
                onClick={() => setPendingAttachments(prev => prev.filter(a => a.id !== attachment.id))}
                className="ml-auto p-1 rounded text-slate-500 hover:text-slate-300 hover:bg-white/10 transition-colors shrink-0"
              >
                <FiX size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Voice Recorder UI ── */}
      {isRecording && (
        <div className="mb-2">
          <VoiceRecorder
            onStop={handleVoiceNote}
            onCancel={() => setIsRecording(false)}
          />
        </div>
      )}

      {/* ── Input row ── */}
      <div
        className={`
          flex items-end gap-2 px-3 py-2 rounded-2xl
          bg-white/6 border transition-all duration-150
          ${isEdit
            ? "border-amber-500/30 focus-within:border-amber-500/60"
            : "border-white/10 focus-within:border-blue-500/40"
          }
        `}
      >
        {/* Attachment Popup Menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowAttachmentMenu((v) => !v)}
            disabled={!selectedConvId || isEdit || isRecording}
            className={`p-1.5 transition-colors shrink-0 mb-0.5 ${showAttachmentMenu
                ? "text-blue-400 bg-blue-500/10 rounded-lg"
                : "text-slate-600 hover:text-slate-400"
              } disabled:opacity-40`}
            title="Attach file"
          >
            <FiPaperclip size={15} />
          </button>

          {showAttachmentMenu && (
            <div className="absolute bottom-full left-0 mb-3 w-56 bg-slate-900 border border-white/10 rounded-2xl shadow-2xl p-2 z-[100] animate-in fade-in slide-in-from-bottom-2 duration-200">
              <div className="grid grid-cols-1 gap-1">
                <MenuOption icon={<FiFile className="text-violet-500" />} label="Document" onClick={() => fileInputRef.current?.click()} />
                <MenuOption icon={<FiImage className="text-blue-500" />} label="Photos & videos" onClick={() => mediaInputRef.current?.click()} />
                <MenuOption icon={<FiCamera className="text-pink-500" />} label="Camera" onClick={() => { setShowCamera(true); setShowAttachmentMenu(false); }} />
                <MenuOption icon={<FiMusic className="text-orange-500" />} label="Audio" onClick={() => audioInputRef.current?.click()} />
                <MenuOption icon={<FiUser className="text-cyan-500" />} label="Contact" onClick={() => { setShowContactPicker(true); setShowAttachmentMenu(false); }} />
                <MenuOption icon={<FiBarChart2 className="text-amber-500" />} label="Poll" onClick={() => { setShowPollModal(true); setShowAttachmentMenu(false); }} />
                <MenuOption icon={<FiCalendar className="text-rose-500" />} label="Event" onClick={() => alert("Events coming soon!")} />
              </div>
            </div>
          )}
        </div>

        {/* Hidden File Inputs */}
        <input type="file" ref={fileInputRef} multiple className="hidden" onChange={(e) => { handleFileUploads(e.target.files); e.target.value = ""; }} />
        <input type="file" ref={mediaInputRef} multiple accept="image/*,video/*" className="hidden" onChange={(e) => { handleFileUploads(e.target.files); e.target.value = ""; }} />
        <input type="file" ref={audioInputRef} multiple accept="audio/*" className="hidden" onChange={(e) => { handleFileUploads(e.target.files); e.target.value = ""; }} />

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={
            !selectedConvId
              ? "Select a conversation…"
              : isEdit
                ? "Edit your message…"
                : connected
                  ? "Type a message…"
                  : "Connecting…"
          }
          disabled={!selectedConvId || sending}
          rows={1}
          className={`
            flex-1 bg-transparent text-sm text-slate-200 placeholder-slate-600
            outline-none resize-none leading-relaxed py-0.5
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
          style={{ maxHeight: "120px" }}
        />

        {/* Emoji Selector */}
        <div className="relative">
          <button
            onClick={() => setShowEmoji((v) => !v)}
            disabled={isRecording}
            className={`p-1.5 transition-colors shrink-0 mb-0.5 ${showEmoji ? "text-blue-400" : "text-slate-600 hover:text-slate-400"
              }`}
            title="Add emoji"
          >
            <FiSmile size={15} />
          </button>
          {showEmoji && (
            <EmojiPicker
              onSelect={(emoji) => setText((prev) => prev + emoji)}
              onSelectSticker={handleSendSticker}
              onClose={() => setShowEmoji(false)}
            />
          )}
        </div>

        {/* Voice Note Button */}
        {!text.trim() && pendingAttachments.length === 0 && !isEdit && (
          <button
            onClick={() => setIsRecording(true)}
            disabled={!selectedConvId || isRecording}
            className="p-1.5 text-slate-600 hover:text-blue-400 transition-colors shrink-0 mb-0.5 disabled:opacity-40"
            title="Record voice note"
          >
            <FiMic size={15} />
          </button>
        )}

        {/* Send / Save button */}
        <button
          onClick={handleSend}
          disabled={!canSend || sending}
          className={`
            p-1.5 rounded-xl transition-all duration-150 shrink-0 mb-0.5
            ${canSend
              ? isEdit
                ? "text-amber-400 hover:bg-amber-500/20 hover:text-amber-300"
                : "text-blue-400 hover:bg-blue-500/20 hover:text-blue-300"
              : "text-slate-700 cursor-not-allowed"
            }
          `}
          title={isEdit ? "Save edit (Enter)" : "Send (Enter)"}
        >
          {sending
            ? <div className="w-3.5 h-3.5 border border-current border-t-transparent rounded-full animate-spin" />
            : <FiSend size={15} />
          }
        </button>
      </div>

      {/* Connection warning */}
      {!connected && !isEdit && selectedConvId && (
        <p className="text-center text-[10px] text-slate-600 mt-1.5">
          Reconnecting to chat server…
        </p>
      )}
      {/* Modals */}
      {showContactPicker && <ContactPickerModal onClose={() => setShowContactPicker(false)} onSelect={handleSelectContact} />}
      {showPollModal && <PollModal onClose={() => setShowPollModal(false)} onCreate={handleCreatePoll} initialData={pollInitialData} />}
      {showCamera && <CameraCaptureModal onClose={() => setShowCamera(false)} onCapture={handleCapture} />}
    </div>
  );
}

function MenuOption({ icon, label, onClick }) {
  // Map labels to colors for circular icons
  const colorMap = {
    "Document": "bg-violet-600",
    "Photos & videos": "bg-blue-600",
    "Camera": "bg-pink-600",
    "Audio": "bg-orange-600",
    "Contact": "bg-cyan-600",
    "Poll": "bg-amber-600",
    "Event": "bg-rose-600"
  };
  const bgColor = colorMap[label] || "bg-slate-700";

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-slate-300 hover:bg-white/5 transition-colors text-xs font-medium group"
    >
      <div className={`w-8 h-8 rounded-full ${bgColor} flex items-center justify-center text-white shadow-lg transition-transform group-hover:scale-110`}>
        {icon}
      </div>
      {label}
    </button>
  );
}